import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { useParams, useSearchParams } from "react-router-dom";
import useDebounce from "../../hooks/useDebounce";
import { fetchCategoryDetails } from "../../hooks/useCategoryDetails";
import api from "../../utils/api/axiosInstance";
import { isDevelopment } from "../../config/env";
import ProductCard from "../../components/product/ProductCard";
import CategoryFilters from "../../components/filters/CategoryFilters";
import ErrorBoundary from "../../components/common/ErrorBoundary";
import PageSeo from "../../components/common/PageSeo";
import JsonLd from "../../components/common/JsonLd";
import { buildBreadcrumbJsonLd } from "../../utils/structuredData";
import AppFeedbackPrompt from "../../components/feedback/AppFeedbackPrompt";
import styles from "../../styles/pages/category/CategoryFiltersPage.module.css";
import ProductSkeleton from "../../components/product/ProductSkeleton";
import capitalizeFirst from "../../utils/capitalizeFirst";
import getImageUrl from "../../utils/imageUtils";
import { useToast } from "../../contexts/ToastContext";
import { useUserAuth } from "../../contexts/UserAuthContext";
import useHomeChatMatchSession from "../../hooks/useHomeChatMatchSession";
import useProductGridMatchScores from "../../hooks/useProductGridMatchScores";
import {
  buildBrowseFitIntent,
  shouldShowGridMatchScores,
} from "../../utils/gridFitIntentUtils";
import {
  FaThLarge,
  FaList,
  FaArrowDown,
  FaArrowUp,
  FaRegCalendarAlt,
  FaSearch,
  FaFilter,
  FaTimes,
} from "react-icons/fa";

// Named constants (avoid scattering magic numbers through the component)
const PRODUCTS_PER_PAGE = 12;
const CATEGORY_LOAD_TIMEOUT_MS = 10000;
const PRODUCTS_FETCH_TIMEOUT_MS = 8000;
const FILTER_DEBOUNCE_MS = 300;
const SKELETON_COUNT = 8;
const FALLBACK_MAX_PRICE = 2000;
const MIN_PRICE_STEP = 1000;

const roundCategoryMaxPrice = (rawMax) => {
  const maxPrice = Number(rawMax) || 0;
  if (maxPrice <= 0) return 0;
  return Math.max(
    MIN_PRICE_STEP,
    Math.ceil(maxPrice / MIN_PRICE_STEP) * MIN_PRICE_STEP,
  );
};
const URL_SYNCED_FILTER_KEYS = [
  "minPrice",
  "maxPrice",
  "brand",
  "model",
  "year",
  "sortBy",
];

//banner

const defaultFilters = {
  minPrice: "",
  maxPrice: "",
  model: [],
  year: [],
  brand: [],
  sortBy: "",
};

// Rebuild filter state from the current URL so filters survive reload/back-forward/share.
const seedFiltersFromUrl = (search) => {
  const params = new URLSearchParams(search);
  const seeded = { ...defaultFilters };
  const sub = params.get("sub");
  if (sub) seeded.subcategory = sub;
  const sortBy = params.get("sortBy");
  if (sortBy) seeded.sortBy = sortBy;
  ["brand", "model", "year"].forEach((key) => {
    const values = params.getAll(key);
    if (values.length > 0) seeded[key] = values;
  });
  const urlMinPrice = params.get("minPrice");
  const urlMaxPrice = params.get("maxPrice");
  if (urlMinPrice !== null) seeded.minPrice = Number(urlMinPrice) || 0;
  if (urlMaxPrice !== null) seeded.maxPrice = Number(urlMaxPrice) || "";
  return seeded;
};

const SORT_OPTION_VALUES = [
  { value: "price-asc", text: "Price: Low to High", Icon: FaArrowDown },
  { value: "price-desc", text: "Price: High to Low", Icon: FaArrowUp },
  { value: "year-desc", text: "Year: Newest", Icon: FaRegCalendarAlt },
  { value: "year-asc", text: "Year: Oldest", Icon: FaRegCalendarAlt },
];

const sortOptions = SORT_OPTION_VALUES.map(({ value, text, Icon }) => ({
  value,
  text,
  label: (
    <>
      <Icon size={20} className={styles.sortIcon} />
      {text}
    </>
  ),
}));

const CategoryFiltersPage = () => {
  const { categoryId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { user } = useUserAuth();
  const aiSession = useHomeChatMatchSession(user);
  const [dynamicFields, setDynamicFields] = useState([]);
  const [viewMode, setViewMode] = useState(
    localStorage.getItem("gridViewMode") || "grid",
  );
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => window.matchMedia?.("(max-width: 1024px)")?.matches ?? false,
  );
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const closeFilterDrawer = useCallback(() => setFilterDrawerOpen(false), []);

  // 🧪 Filters state - seeded from URL so filters are shareable/bookmarkable
  const [filters, setFilters] = useState(() =>
    seedFiltersFromUrl(window.location.search),
  );
  // Whether price came from the URL on first load (skip re-seeding it from category max-price)
  const priceSeededFromUrlRef = useRef(
    new URLSearchParams(window.location.search).get("maxPrice") !== null,
  );

  // Subcategory from URL
  const activeSubSlug = searchParams.get("sub") || "";

  // 🔸 Data and UI States
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [showMoreLoading, setShowMoreLoading] = useState(false);
  const [showMoreError, setShowMoreError] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const limit = PRODUCTS_PER_PAGE;
  const debouncedFilters = useDebounce(filters, FILTER_DEBOUNCE_MS);
  const pendingInitialFetchRef = useRef(false);
  const [categoryName, setCategoryName] = useState("");
  const [resetTrigger, setResetTrigger] = useState(0);
  const [category, setCategory] = useState(null);
  const [categoryMaxPrice, setCategoryMaxPrice] = useState(0);
  const [readyForProductFetch, setReadyForProductFetch] = useState(false);
  const categoryMaxPriceRef = useRef(FALLBACK_MAX_PRICE);
  const productsFetchIdRef = useRef(0);
  const categoryLoadIdRef = useRef(0);
  const activeUrlSubRef = useRef("");

  useEffect(() => {
    const desktopMq = window.matchMedia?.("(min-width: 769px)");
    const drawerMq = window.matchMedia?.("(max-width: 1024px)");

    const syncPageRootClass = () => {
      const enabled = Boolean(desktopMq?.matches);
      document.documentElement.classList.toggle(
        "category-filters-page-root",
        enabled,
      );
      document.body.classList.toggle("category-filters-page-root", enabled);
    };
    const syncDrawerViewport = () => {
      const isDrawer = Boolean(drawerMq?.matches);
      setIsMobileViewport(isDrawer);
      if (!isDrawer) setFilterDrawerOpen(false);
    };

    syncPageRootClass();
    syncDrawerViewport();
    desktopMq?.addEventListener("change", syncPageRootClass);
    drawerMq?.addEventListener("change", syncDrawerViewport);
    return () => {
      desktopMq?.removeEventListener("change", syncPageRootClass);
      drawerMq?.removeEventListener("change", syncDrawerViewport);
      document.documentElement.classList.remove("category-filters-page-root");
      document.body.classList.remove("category-filters-page-root");
    };
  }, []);

  useEffect(() => {
    if (!filterDrawerOpen) return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeFilterDrawer();
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [filterDrawerOpen, closeFilterDrawer]);

  const buildFilterParams = useCallback((nextFilters, nextLimit, nextSkip) => {
    const params = new URLSearchParams();
    Object.entries(nextFilters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, v));
      } else if (value !== "") {
        params.append(key, value);
      }
    });
    params.set("limit", nextLimit);
    params.set("skip", nextSkip);
    if (isDevelopment) {
      params.set("nocache", "1");
    }
    return params;
  }, []);

  const readUrlSub = useCallback(
    () =>
      searchParams.get("sub") ||
      new URLSearchParams(window.location.search).get("sub") ||
      "",
    [searchParams],
  );

  const resolveFiltersForRequest = useCallback(
    (nextFilters) => {
      const urlSub = readUrlSub() || activeUrlSubRef.current;
      if (!nextFilters.subcategory && urlSub) {
        return { ...nextFilters, subcategory: urlSub };
      }
      return nextFilters;
    },
    [readUrlSub],
  );

  useEffect(() => {
    activeUrlSubRef.current = readUrlSub();
  }, [readUrlSub]);

  // 🔹 Persist View Mode
  useEffect(() => {
    localStorage.setItem("gridViewMode", viewMode);
  }, [viewMode]);

  // Load category metadata and seed filters — products load via debouncedFilters effect
  useEffect(() => {
    if (!categoryId) return;

    const controller = new AbortController();
    let active = true;
    const loadId = ++categoryLoadIdRef.current;
    activeUrlSubRef.current =
      searchParams.get("sub") ||
      new URLSearchParams(window.location.search).get("sub") ||
      activeUrlSubRef.current ||
      "";
    setReadyForProductFetch(false);
    setLoading(true);
    setProducts([]);
    setCategory(null);
    setCategoryMaxPrice(0);

    const loadCategoryData = async () => {
      try {
        const timeoutId = setTimeout(
          () => controller.abort(),
          CATEGORY_LOAD_TIMEOUT_MS,
        );

        // Fetch category details and the price ceiling concurrently (rather
        // than sequentially) so the first product fetch only fires once,
        // using the real max price instead of a fallback that immediately
        // gets superseded and triggers a redundant second product fetch.
        const maxPricePromise = api
          .get(`/products/max-price/${categoryId}`, {
            signal: controller.signal,
          })
          .then((maxPriceRes) => {
            const rounded = roundCategoryMaxPrice(maxPriceRes.data.maxPrice);
            return { rounded, failed: false };
          })
          .catch((maxPriceErr) => {
            if (
              maxPriceErr?.name === "CanceledError" ||
              maxPriceErr?.code === "ERR_CANCELED"
            ) {
              throw maxPriceErr;
            }
            console.error("Failed to fetch category max price:", maxPriceErr);
            return { rounded: FALLBACK_MAX_PRICE, failed: true };
          });

        const [categoryData, maxPriceResult] = await Promise.all([
          fetchCategoryDetails(categoryId, { signal: controller.signal }),
          maxPricePromise,
        ]);

        clearTimeout(timeoutId);
        if (
          !active ||
          controller.signal.aborted ||
          loadId !== categoryLoadIdRef.current
        )
          return;

        setCategory(categoryData);
        setCategoryName(categoryData.name || "Loading...");
        setDynamicFields(categoryData.fields || []);

        const urlSub =
          searchParams.get("sub") ||
          new URLSearchParams(window.location.search).get("sub") ||
          activeUrlSubRef.current ||
          "";

        if (loadId !== categoryLoadIdRef.current) return;

        categoryMaxPriceRef.current = maxPriceResult.rounded;
        setCategoryMaxPrice(maxPriceResult.rounded);
        if (maxPriceResult.failed) {
          showToast({
            message:
              "Couldn't load the price range for this category. Using a default range instead.",
            type: "error",
          });
        }

        const urlSeededFilters = seedFiltersFromUrl(window.location.search);
        setFilters((prev) => ({
          ...urlSeededFilters,
          minPrice: priceSeededFromUrlRef.current
            ? urlSeededFilters.minPrice
            : 0,
          maxPrice: priceSeededFromUrlRef.current
            ? urlSeededFilters.maxPrice
            : maxPriceResult.rounded || "",
          subcategory: urlSub || prev.subcategory || "",
        }));
        pendingInitialFetchRef.current = true;
        setReadyForProductFetch(true);
      } catch (err) {
        if (!active || controller.signal.aborted) return;
        console.error("Failed to load category data:", err);
        if (err.name === "AbortError") {
          setCategoryName("Loading Timeout");
        } else {
          setCategoryName("Category Not Found");
        }
        setCategory(null);
        setDynamicFields([]);
        setProducts([]);
      } finally {
        if (
          active &&
          !controller.signal.aborted &&
          loadId === categoryLoadIdRef.current
        ) {
          setLoading(false);
        }
      }
    };

    loadCategoryData();

    return () => {
      active = false;
      controller.abort();
      categoryLoadIdRef.current += 1;
    };
    // searchParams read at runtime for ?sub= on first load; chip clicks update filters directly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, buildFilterParams, limit]);

  // Keep filter subcategory in sync when ?sub= changes without a category reload
  useEffect(() => {
    if (!readyForProductFetch) return;
    const urlSub = readUrlSub();
    setFilters((prev) => {
      if ((prev.subcategory || "") === urlSub) return prev;
      return { ...prev, subcategory: urlSub };
    });
  }, [readUrlSub, readyForProductFetch]);

  // Keep a live ref of the latest filters so the fetch effect can read the immediate
  // (non-debounced) value on the very first fetch without needing `filters` in its deps
  // (which previously caused a redundant extra request on every filter change).
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const [productsLoading, setProductsLoading] = useState(true);
  const [productsLoadError, setProductsLoadError] = useState(null);
  const [productsRetry, setProductsRetry] = useState(0);

  useEffect(() => {
    if (!readyForProductFetch) return;

    const fetchId = ++productsFetchIdRef.current;
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, PRODUCTS_FETCH_TIMEOUT_MS);
    const hadProductsAlready = products.length > 0;

    const fetchByFilters = async () => {
      setProductsLoading(true);
      setProductsLoadError(null);
      if (hadProductsAlready) setIsRefetching(true);
      try {
        const useImmediateFilters = pendingInitialFetchRef.current;
        if (useImmediateFilters) {
          pendingInitialFetchRef.current = false;
        }
        const requestFilters = resolveFiltersForRequest(
          useImmediateFilters ? filtersRef.current : debouncedFilters,
        );
        const params = buildFilterParams(requestFilters, limit, 0);
        const expectedSub = requestFilters.subcategory || "";
        const res = await api.get(
          `/products/category/${categoryId}?${params}`,
          { signal: controller.signal },
        );

        clearTimeout(timeoutId);
        if (fetchId !== productsFetchIdRef.current) return;
        const intentSub = activeUrlSubRef.current;
        if (intentSub && expectedSub !== intentSub) {
          return;
        }
        const urlSubNow = readUrlSub();
        if (urlSubNow && expectedSub !== urlSubNow) return;
        setProducts(res.data);
        const headerTotal = Number(res.headers?.["x-total-count"]);
        setTotalCount(
          Number.isFinite(headerTotal) && headerTotal >= 0
            ? headerTotal
            : res.data.length,
        );
        setSkip(limit);
        setHasMore(res.data.length === limit);
      } catch (err) {
        if (fetchId !== productsFetchIdRef.current) return;
        const isAbort =
          err.name === "AbortError" ||
          err.name === "CanceledError" ||
          err.code === "ERR_CANCELED";
        if (isAbort) {
          if (!timedOut) return;
          console.error("Product fetch timed out:", err);
          setProducts([]);
          setTotalCount(0);
          setHasMore(false);
          setProductsLoadError(
            "Loading products took too long. Please try again.",
          );
          showToast({
            message: "Loading products took too long. Please try again.",
            type: "error",
          });
          return;
        }
        console.error("Failed to fetch products:", err);
        if (!hadProductsAlready) {
          setProductsLoadError(
            "We couldn't load products for this category. Please try again.",
          );
        }
        showToast({
          message: "Failed to load products for this category.",
          type: "error",
        });
      } finally {
        if (fetchId === productsFetchIdRef.current) {
          setProductsLoading(false);
          setIsRefetching(false);
        }
      }
    };

    fetchByFilters();

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    readyForProductFetch,
    categoryId,
    debouncedFilters,
    limit,
    buildFilterParams,
    resolveFiltersForRequest,
    readUrlSub,
    productsRetry,
  ]);

  // Load More Products Handler - memoized with useCallback
  const handleShowMore = useCallback(async () => {
    setShowMoreLoading(true);
    setShowMoreError(false);
    try {
      const params = buildFilterParams(
        resolveFiltersForRequest(filters),
        limit,
        skip,
      );
      const res = await api.get(
        `/products/category/${encodeURIComponent(
          categoryId,
        )}?${params.toString()}`,
      );

      const newProducts = res.data;

      if (newProducts.length < limit) {
        setHasMore(false);
      }

      setProducts((prev) => [...prev, ...newProducts]);
      setSkip((prev) => prev + limit);
    } catch (err) {
      console.error("Failed to load more products:", err);
      setShowMoreError(true);
      showToast({
        message: "Failed to load more products. Please try again.",
        type: "error",
      });
    } finally {
      setShowMoreLoading(false);
    }
  }, [
    buildFilterParams,
    filters,
    skip,
    categoryId,
    limit,
    resolveFiltersForRequest,
    showToast,
  ]);

  // Handlers - memoized with useCallback
  const handleReset = useCallback(() => {
    setFilters({
      ...defaultFilters,
      minPrice: 0,
      maxPrice: categoryMaxPriceRef.current || "",
      subcategory: "",
    });
    setResetTrigger((prev) => prev + 1);
    // Clear subcategory and all shareable filter params from the URL
    const next = new URLSearchParams(searchParams);
    next.delete("sub");
    URL_SYNCED_FILTER_KEYS.forEach((key) => next.delete(key));
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  // Handle subcategory click
  const handleSubcategoryClick = useCallback(
    (subSlug) => {
      const isActive = activeSubSlug === subSlug;
      const next = new URLSearchParams(searchParams);

      if (isActive) {
        next.delete("sub");
        setFilters((prev) => ({ ...prev, subcategory: "" }));
      } else {
        next.set("sub", subSlug);
        setFilters((prev) => ({ ...prev, subcategory: subSlug }));
      }

      setSearchParams(next);
    },
    [activeSubSlug, searchParams, setSearchParams],
  );

  const handleSortClick = useCallback((value) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: prev.sortBy === value ? "" : value,
    }));
  }, []);

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  // Reflect shareable filters (price/brand/model/year/sort) into the URL so the
  // page can be bookmarked/shared/refreshed without losing the current filter state.
  useEffect(() => {
    if (!readyForProductFetch) return;
    const next = new URLSearchParams(window.location.search);
    URL_SYNCED_FILTER_KEYS.forEach((key) => next.delete(key));

    if (debouncedFilters.minPrice !== "" && debouncedFilters.minPrice !== 0) {
      next.set("minPrice", debouncedFilters.minPrice);
    }
    if (
      debouncedFilters.maxPrice !== "" &&
      debouncedFilters.maxPrice !== categoryMaxPriceRef.current
    ) {
      next.set("maxPrice", debouncedFilters.maxPrice);
    }
    ["brand", "model", "year"].forEach((key) => {
      (debouncedFilters[key] || []).forEach((v) => next.append(key, v));
    });
    if (debouncedFilters.sortBy) {
      next.set("sortBy", debouncedFilters.sortBy);
    }

    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilters, readyForProductFetch]);

  // Memoized banner image URL
  const bannerImageUrl = useMemo(
    () => getImageUrl(category?.bannerImage),
    [category?.bannerImage],
  );

  // Memoized category title
  const categoryTitle = useMemo(
    () => capitalizeFirst(categoryName),
    [categoryName],
  );

  const fitIntentParams = useMemo(
    () =>
      buildBrowseFitIntent({
        categoryName: categoryTitle,
        aiSession,
        user,
      }),
    [categoryTitle, aiSession, user],
  );

  const showMatchScores = shouldShowGridMatchScores(fitIntentParams);

  // Batches match-score lookups for all cards on this page into a single
  // request instead of one match-explanation call per product card.
  const { getMatchScore } = useProductGridMatchScores({ products });

  // Memoized product grid/list class name
  const productContainerClass = useMemo(
    () => (viewMode === "grid" ? styles.productGrid : styles.productList),
    [viewMode],
  );

  // Memoized skeleton container class
  const skeletonContainerClass = useMemo(
    () => (viewMode === "grid" ? styles.productGrid : styles.productCardList),
    [viewMode],
  );

  // Show loading state while category is loading
  if (loading && !category) {
    return (
      <div className={`pageContainer ${styles.pageShell}`}>
        <div className="innerContainer">
          <div className={styles.banner}>
            <h1 className={styles.categoryTitle}>Loading...</h1>
          </div>
          <div className={styles.loading}>
            <div
              className={styles.spinner}
              role="status"
              aria-label="Loading category"
            />
          </div>
        </div>
      </div>
    );
  }

  // Show error state if category failed to load
  if (!category && !loading) {
    return (
      <div className={`pageContainer ${styles.pageShell}`}>
        <div className="innerContainer">
          <div className={styles.banner}>
            <h1 className={styles.categoryTitle}>Category Not Found</h1>
          </div>
          <div className={styles.categoryNotFound}>
            <h3>Sorry, we couldn't load this category.</h3>
            <p>Please check the URL or try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  const filterPanel =
    categoryMaxPrice > 0 ? (
      <CategoryFilters
        filters={filters}
        setFilters={setFilters}
        dynamicFields={dynamicFields}
        categoryId={categoryId}
        onReset={resetTrigger}
        seedMaxPrice={categoryMaxPrice}
      />
    ) : null;

  return (
    <ErrorBoundary>
      <PageSeo
        title={category?.seoTitle || categoryTitle}
        description={
          category?.seoDescription ||
          `Browse ${categoryTitle} products on Simpary.`
        }
        pathname={`/category/${categoryId}`}
        imagePath={category?.bannerImage || undefined}
      />
      <JsonLd
        data={buildBreadcrumbJsonLd([
          { name: "Home", pathname: "/" },
          {
            name: categoryTitle || "Category",
            pathname: `/category/${categoryId}`,
          },
        ])}
      />
      <div className={`pageContainer ${styles.pageShell}`}>
        <div className="innerContainer">
          <div
            className={`${styles.banner} ${!category?.bannerImage ? styles.bannerNoImage : ""}`}
          >
            <h1 className={styles.categoryTitle}>{categoryTitle}</h1>
            {category?.bannerImage && (
              <img
                alt={`${categoryTitle} banner`}
                src={bannerImageUrl}
                onError={(e) => {
                  e.currentTarget.src = "/fallback.png";
                  e.currentTarget.onerror = null;
                }}
              />
            )}
          </div>

          {/* Subcategory Filter Bar */}
          {category?.subcategories &&
            category.subcategories.length > 0 &&
            category.subcategories.filter((sub) => sub.isActive !== false)
              .length > 0 && (
              <div className={styles.subcategoryFilterBar}>
                <div className={styles.subcategoryFilterBarHeader}>
                  <span className={styles.subcategoryLabel}>
                    Subcategories:
                  </span>
                </div>
                <div className={styles.subcategoryChips}>
                  {category.subcategories
                    .filter((sub) => sub.isActive !== false)
                    .map((sub) => {
                      const isActive =
                        (filters.subcategory || activeSubSlug) === sub.slug;
                      return (
                        <button
                          key={sub._id || sub.slug}
                          type="button"
                          className={`${styles.subcategoryFilterChip} ${
                            isActive ? styles.activeSub : ""
                          }`}
                          onClick={() => handleSubcategoryClick(sub.slug)}
                        >
                          {sub.name}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}

          {/* filters side*/}
          <div className={styles.contentWrapper}>
            {!isMobileViewport && (
              <aside className={styles.sidebar} aria-label="Filter products">
                <h3>Filter products</h3>
                {filterPanel}
              </aside>
            )}

            {isMobileViewport &&
              filterDrawerOpen &&
              createPortal(
                <div className={styles.mobileDrawerRoot}>
                  <div
                    className={styles.mobileDrawerBackdrop}
                    onClick={closeFilterDrawer}
                    aria-hidden="true"
                  />
                  <div
                    className={styles.mobileDrawerPanel}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Filter products"
                  >
                    <div className={styles.mobileDrawerHeader}>
                      <h2 className={styles.mobileDrawerTitle}>
                        Filter products
                      </h2>
                      <button
                        type="button"
                        className={styles.mobileDrawerClose}
                        onClick={closeFilterDrawer}
                        aria-label="Close filters"
                      >
                        <FaTimes aria-hidden="true" />
                      </button>
                    </div>
                    <div className={styles.mobileDrawerBody}>{filterPanel}</div>
                  </div>
                </div>,
                document.body,
              )}

            {/* product side */}

            <main className={styles.productArea}>
              <div className={styles.Toolbar}>
                <div className={styles.toolbarLeft}>
                  <button
                    type="button"
                    className={styles.mobileFilterButton}
                    onClick={() => setFilterDrawerOpen(true)}
                    aria-expanded={filterDrawerOpen}
                    aria-haspopup="dialog"
                    aria-label="Open filters"
                  >
                    <FaFilter aria-hidden="true" />
                    <span>Filters</span>
                  </button>
                  <div className={styles.viewToggle}>
                    <button
                      type="button"
                      onClick={() => handleViewModeChange("grid")}
                      className={
                        viewMode === "grid"
                          ? styles.toggleActive
                          : styles.toggleButton
                      }
                      aria-label="Grid view"
                      aria-pressed={viewMode === "grid"}
                      title="Grid view"
                    >
                      <FaThLarge />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleViewModeChange("list")}
                      className={
                        viewMode === "list"
                          ? styles.toggleActive
                          : styles.toggleButton
                      }
                      aria-label="List view"
                      aria-pressed={viewMode === "list"}
                      title="List view"
                    >
                      <FaList />
                    </button>
                  </div>
                  {!productsLoading && products.length > 0 && (
                    <div className={styles.productCount}>
                      <strong>{totalCount || products.length}</strong> product
                      {(totalCount || products.length) !== 1 ? "s" : ""} found
                      {isRefetching && (
                        <span aria-live="polite"> · updating…</span>
                      )}
                    </div>
                  )}
                </div>

                <div className={styles.categoryFiltersSorting}>
                  <div className={styles.sortChipRow}>
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        aria-pressed={filters.sortBy === option.value}
                        className={`buttonSort ${
                          filters.sortBy === option.value ? "active" : ""
                        }`}
                        onClick={() => handleSortClick(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <label className={styles.mobileSortWrap}>
                    <span className={styles.mobileSortLabel}>Sort</span>
                    <select
                      className={styles.mobileSortSelect}
                      aria-label="Sort products"
                      value={filters.sortBy || ""}
                      onChange={(event) =>
                        setFilters((prev) => ({
                          ...prev,
                          sortBy: event.target.value,
                        }))
                      }
                    >
                      <option value="">Default</option>
                      {SORT_OPTION_VALUES.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.text}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    className={`buttonReset ${styles.resetButton}`}
                    onClick={handleReset}
                    type="button"
                  >
                    Reset Filters ↺
                  </button>
                </div>
              </div>

              {productsLoading && products.length === 0 ? (
                <div className={skeletonContainerClass}>
                  {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                    <ProductSkeleton key={i} view={viewMode} />
                  ))}
                </div>
              ) : productsLoadError && products.length === 0 ? (
                <div className={styles.emptyState} role="alert">
                  <div className={styles.emptyIcon}>
                    <FaSearch />
                  </div>
                  <h3>Couldn&apos;t load products</h3>
                  <p>{productsLoadError}</p>
                  <button
                    className="buttonEmptyState"
                    onClick={() => setProductsRetry((attempt) => attempt + 1)}
                    type="button"
                  >
                    Retry
                  </button>
                </div>
              ) : products.length === 0 ? (
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}>
                    <FaSearch />
                  </div>
                  <h3>No products found</h3>
                  <p>Try adjusting your filters or browse other categories</p>
                  <button
                    className="buttonEmptyState"
                    onClick={handleReset}
                    type="button"
                  >
                    Clear All Filters
                  </button>
                </div>
              ) : (
                <>
                  <div className={productContainerClass}>
                    {products.map((product) => (
                      <ProductCard
                        categoryId={categoryId}
                        categoryName={categoryName}
                        key={product._id}
                        product={product}
                        view={viewMode}
                        showMatchScore={showMatchScores}
                        precomputedMatchScore={getMatchScore(product._id)}
                        openInNewTab
                      />
                    ))}
                  </div>

                  {hasMore && (
                    <div className="buttonShowMoreContainer">
                      <button
                        onClick={handleShowMore}
                        className="buttonShowMore"
                        disabled={showMoreLoading}
                        type="button"
                      >
                        {showMoreLoading ? "Loading…" : "Show More"}
                      </button>
                      {showMoreError && (
                        <p className={styles.showMoreError} role="alert">
                          Couldn't load more products. Please try again.
                        </p>
                      )}
                    </div>
                  )}
                  <AppFeedbackPrompt
                    source="category"
                    prompt="How is this category page working for you?"
                    metadata={{
                      categoryId,
                      categoryName: categoryTitle,
                      resultCount: totalCount || products.length,
                    }}
                  />
                </>
              )}
            </main>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default CategoryFiltersPage;
